import { Button, DatePicker, Empty, Flex, Segmented, Skeleton, Table, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import { DownloadOutlined } from "@ant-design/icons"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  Tooltip as RechartsTooltip,
  ReferenceArea,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts"

import { budgetFormat } from "../../../utils/number/budget-format.ts"
import { chartSeries, neutral, semantic } from "../tokens"
import { csvEscape, triggerCsvDownload } from "../widgetCsv"
import { MarketingSourceTable } from "./MarketingSourceTable"
import type {
  CashFlowPoint,
  EngagementData,
  EngagementDrill,
  EngagementGroup,
  EngagementGroupRow,
  ProfitabilityBucket,
  ProjectStatusData,
  SeriesGranularity,
  TrendPoint,
  WidgetProps,
} from "../types"

type ChartHeight = number | `${number}%`

const DEFAULT_CHART_HEIGHT = 288

const COMPARE_STROKE = neutral.faint
const COMPARE_DASH = "4 4"

// Recharts drops ticks that would collide, so a 13-week or 12-month axis keeps every
// label while a 31-day one thins itself out instead of overprinting.
const AXIS_MIN_TICK_GAP = 24

const AXIS_TICK_OPTIONS: Record<SeriesGranularity, Intl.DateTimeFormatOptions> = {
  day: { day: "numeric", month: "short" },
  week: { day: "numeric", month: "short" },
  month: { month: "short", year: "numeric" }
}

// An ISO date alone parses as UTC midnight, which renders as the previous day west of
// Greenwich; these labels are calendar dates, not instants.
const parseBucketDate = (value: string): Date => new Date(`${value}T00:00:00`)

const bucketLabeller = (locale: string | undefined, granularity: SeriesGranularity) => {
  const format = new Intl.DateTimeFormat(locale, AXIS_TICK_OPTIONS[granularity]).format
  return (value: string): string => {
    const parsed = parseBucketDate(value)
    return Number.isNaN(parsed.getTime()) ? value : format(parsed)
  }
}

const PROJECTED_FILL = neutral.faint
const PROJECTED_FILL_OPACITY = 0.08

// Index of the first bucket that starts after `asOf`. The bucket containing `asOf` is
// part actual, part projected and stays unshaded — it is the period still in progress.
export const firstProjectedIndex = (series: CashFlowPoint[], asOf?: string): number => {
  if (!asOf) return -1
  return series.findIndex((point) => point.date > asOf)
}

type CashFlowVariant = "line" | "area" | "bar"

interface CashFlowChartProps {
  series: CashFlowPoint[]
  currency?: string
  variant?: CashFlowVariant
  granularity?: SeriesGranularity
  asOf?: string
  compareSeries?: CashFlowPoint[]
  compareLabel?: string
  height?: ChartHeight
}

const CashFlowChartImpl = ({
  series,
  currency = "USD",
  variant = "bar",
  granularity = "day",
  asOf,
  compareSeries,
  compareLabel,
  height = DEFAULT_CHART_HEIGHT
}: CashFlowChartProps) => {
  const { t, i18n } = useTranslation()

  // A window with no movements arrives as a row of zero points; drawn, it is a
  // flat line on an axis the chart invents ($0–$4), which reads as data.
  const noMovement = (points: CashFlowPoint[] = []) =>
    points.every((point) => !point.in && !point.out && !point.net)
  if (!series.length || (noMovement(series) && noMovement(compareSeries))) {
    return (
      <Flex
        align="center"
        justify="center"
        data-testid="cash-flow-empty"
        style={{ height: "100%", minHeight: CHART_EMPTY_MIN_HEIGHT }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          styles={{ root: { margin: 0 }, image: { height: 44 } }}
          description={t("portfolio.cashFlow.empty", "No cash-flow data for this window")}
        />
      </Flex>
    )
  }

  const formatCurrency = (value: number) =>
    budgetFormat(value, { currency, notation: "compact" })

  // Cash-flow buckets are sparse (one per active due-date), so a compare window can
  // yield a different bucket count; index-zipping would then pair unrelated dates.
  const hasCompare =
    Array.isArray(compareSeries) && compareSeries.length === series.length
  const data = series.map((point, i) => ({
    ...point,
    compareNet: hasCompare ? compareSeries?.[i]?.net : undefined
  }))
  const inName = t("portfolio.cashFlow.in", "In")
  const outName = t("portfolio.cashFlow.out", "Out")

  const labelBucket = bucketLabeller(i18n?.language, granularity)
  const projectedFrom = firstProjectedIndex(series, asOf)
  const projectedLabel = t("portfolio.cashFlow.projected", "Projected")
  const tooltipLabel = (value: string) => {
    const base =
      granularity === "week"
        ? t("portfolio.cashFlow.weekOf", "Week of {{date}}", { date: labelBucket(value) })
        : labelBucket(value)
    return asOf && value > asOf ? `${base} · ${projectedLabel}` : base
  }

  const flowMarks =
    variant === "line" ? (
      <>
        <Line type="monotone" dataKey="in" name={inName} stroke={chartSeries.cashIn} strokeWidth={2} dot={{ r: 3 }} />
        <Line type="monotone" dataKey="out" name={outName} stroke={chartSeries.cashOut} strokeWidth={2} dot={{ r: 3 }} />
      </>
    ) : variant === "area" ? (
      <>
        <Area type="monotone" dataKey="in" name={inName} stroke={chartSeries.cashIn} fill={chartSeries.cashIn} fillOpacity={0.15} strokeWidth={2} />
        <Area type="monotone" dataKey="out" name={outName} stroke={chartSeries.cashOut} fill={chartSeries.cashOut} fillOpacity={0.15} strokeWidth={2} />
      </>
    ) : (
      <>
        <Bar dataKey="in" name={inName} fill={chartSeries.cashIn} radius={[4, 4, 0, 0]} />
        <Bar dataKey="out" name={outName} fill={chartSeries.cashOut} radius={[4, 4, 0, 0]} />
      </>
    )

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={neutral.border} vertical={false} />
        {projectedFrom > 0 && (
          <ReferenceArea
            data-testid="cash-flow-projected-region"
            x1={series[projectedFrom].date}
            x2={series[series.length - 1].date}
            fill={PROJECTED_FILL}
            fillOpacity={PROJECTED_FILL_OPACITY}
            strokeOpacity={0}
            label={{
              value: projectedLabel,
              position: "insideTopRight",
              fontSize: 11,
              fill: neutral.muted
            }}
          />
        )}
        <XAxis
          dataKey="date"
          tickFormatter={labelBucket}
          minTickGap={AXIS_MIN_TICK_GAP}
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={{ stroke: neutral.border }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={false}
          tickLine={false}
          width={72}
        />
        <RechartsTooltip
          formatter={(value) => formatCurrency(Number(value))}
          labelFormatter={(value) => tooltipLabel(String(value))}
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${neutral.border}`,
            boxShadow: "none"
          }}
        />
        <Legend
          verticalAlign="top"
          align="right"
          iconType="circle"
          wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
        />
        {flowMarks}
        <Line
          type="monotone"
          dataKey="net"
          name={t("portfolio.cashFlow.net", "Net")}
          stroke={chartSeries.net}
          strokeWidth={2}
          dot={{ r: 3 }}
        />
        {hasCompare && (
          <Line
            type="monotone"
            dataKey="compareNet"
            name={compareLabel}
            stroke={COMPARE_STROKE}
            strokeDasharray={COMPARE_DASH}
            strokeWidth={2}
            dot={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export const CashFlowChart = React.memo(CashFlowChartImpl)

interface ProjectStatusDonutProps {
  data: ProjectStatusData
  height?: ChartHeight
}

const DONUT_HEIGHT = 104
const DONUT_MIN_RING_HEIGHT = 60
// The ring and its legend sit side by side while the card is wide enough for
// both, and wrap to a stack below that — the legend is the readable half, so
// it keeps a floor of its own rather than being squeezed to a column of digits.
const DONUT_MIN_RING_WIDTH = 124
const DONUT_LEGEND_MIN_WIDTH = 124
const DONUT_TOOLTIP_Z = 1
const CHART_EMPTY_MIN_HEIGHT = 140

// Muted sage / sand / dusty rose. The label beside each swatch is what
// carries the meaning — colour alone never does.
const SLICES = [
  { key: "on_track" as const, i18nKey: "portfolio.status.on_track", fallback: "On Track", color: "#43A477" },
  { key: "at_risk" as const, i18nKey: "portfolio.status.at_risk", fallback: "At Risk", color: "#D3A04F" },
  { key: "delayed" as const, i18nKey: "portfolio.status.delayed", fallback: "Delayed", color: "#D46D78" },
]

const ProjectStatusDonutImpl = ({
  data,
  height = DONUT_HEIGHT
}: ProjectStatusDonutProps) => {
  const { t } = useTranslation()
  const fills = height === "100%"

  const chartData = SLICES.map((s) => ({
    name: t(s.i18nKey, s.fallback),
    value: data[s.key],
    color: s.color,
  }))

  const total = chartData.reduce((sum, d) => sum + d.value, 0)

  const hasData = chartData.some((d) => d.value > 0)

  if (!hasData) {
    return (
      <Flex
        align="center"
        justify="center"
        style={{ minHeight: CHART_EMPTY_MIN_HEIGHT }}
      >
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          styles={{ root: { margin: 0 }, image: { height: 44 } }}
          description={t("portfolio.status.empty", "No projects yet")}
        />
      </Flex>
    )
  }

  return (
    <div
      data-testid="project-status-donut"
      style={
        fills
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : {
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16
            }
      }
    >
      <div
        data-testid="project-status-donut-ring"
        style={
          fills
            ? {
                position: "relative",
                flex: "1 1 auto",
                minHeight: DONUT_MIN_RING_HEIGHT
              }
            : {
                position: "relative",
                flex: `1 1 ${DONUT_MIN_RING_WIDTH}px`,
                minWidth: DONUT_MIN_RING_WIDTH
              }
        }
      >
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              innerRadius="62%"
              outerRadius="92%"
              startAngle={90}
              endAngle={-270}
              strokeWidth={0}
            >
              {chartData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <RechartsTooltip
              formatter={(value, name) => {
                const pct = total > 0 ? Math.round((Number(value) / total) * 100) : 0
                return [`${value} (${pct}%)`, name]
              }}
              // The centre-label overlay is a later positioned sibling, so without a
              // stacking order of its own the tooltip renders underneath it.
              wrapperStyle={{ zIndex: DONUT_TOOLTIP_Z }}
              contentStyle={{
                borderRadius: 8,
                border: `1px solid ${neutral.border}`,
                boxShadow: "none",
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <span
            data-testid="donut-total"
            style={{
              fontSize: 24,
              fontWeight: 700,
              lineHeight: "30px",
              fontVariantNumeric: "tabular-nums",
              color: neutral.ink
            }}
          >
            {data.total}
          </span>
          <span style={{ fontSize: 11, lineHeight: "15px", fontWeight: 500, color: neutral.muted }}>
            {t("portfolio.status.projects", "projects")}
          </span>
        </div>
      </div>
      <div
        data-testid="project-status-donut-legend"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 7,
          flexShrink: 0,
          ...(fills
            ? { marginTop: 8 }
            : { flex: `0 1 auto`, minWidth: DONUT_LEGEND_MIN_WIDTH })
        }}
      >
        {SLICES.map((s) => (
          <div
            key={s.key}
            style={{
              display: "grid",
              gridTemplateColumns: "max-content max-content",
              alignItems: "center",
              columnGap: 12,
              fontSize: 13,
              lineHeight: "19px",
              fontWeight: 500
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span
                data-testid={`status-dot-${s.key}`}
                style={{
                  display: "inline-block",
                  flex: "none",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: s.color,
                }}
              />
              <span style={{ color: neutral.muted }}>{t(s.i18nKey, s.fallback)}</span>
            </span>
            <span
              style={{
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                color: neutral.ink
              }}
            >
              {data[s.key]}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export const ProjectStatusDonut = React.memo(ProjectStatusDonutImpl)

type TrendVariant = "line" | "area" | "bar"

interface TrendChartProps {
  series: TrendPoint[]
  currency?: string
  variant?: TrendVariant
  compareSeries?: TrendPoint[]
  compareLabel?: string
  height?: ChartHeight
}

const TrendChartImpl = ({
  series,
  currency = "USD",
  variant = "line",
  compareSeries,
  compareLabel,
  height = DEFAULT_CHART_HEIGHT
}: TrendChartProps) => {
  const { t } = useTranslation()

  if (!series.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("portfolio.chart.empty", "No data for this window")}
      />
    )
  }

  const formatCurrency = (value: number) =>
    budgetFormat(value, { currency, notation: "compact" })

  const hasCompare = Array.isArray(compareSeries) && compareSeries.length > 0
  const data = series.map((point, i) => ({
    ...point,
    compareValue: hasCompare ? compareSeries?.[i]?.value : undefined
  }))
  const currentLabel = t("portfolio.chart.current", "Current")

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={neutral.border} vertical={false} />
        <XAxis
          dataKey="date"
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={{ stroke: neutral.border }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={false}
          tickLine={false}
          width={72}
        />
        <RechartsTooltip
          formatter={(value) => formatCurrency(Number(value))}
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${neutral.border}`,
            boxShadow: "none",
          }}
        />
        {hasCompare && (
          <Legend
            verticalAlign="top"
            align="right"
            iconType="plainline"
            wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
          />
        )}
        {variant === "bar" ? (
          <Bar dataKey="value" name={currentLabel} fill={chartSeries.net} radius={[4, 4, 0, 0]} />
        ) : variant === "area" ? (
          <Area
            type="monotone"
            dataKey="value"
            name={currentLabel}
            stroke={chartSeries.net}
            fill={chartSeries.net}
            fillOpacity={0.12}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        ) : (
          <Line
            type="monotone"
            dataKey="value"
            name={currentLabel}
            stroke={chartSeries.net}
            strokeWidth={2}
            dot={{ r: 3 }}
          />
        )}
        {hasCompare && (
          <Line
            type="monotone"
            dataKey="compareValue"
            name={compareLabel}
            stroke={COMPARE_STROKE}
            strokeDasharray={COMPARE_DASH}
            strokeWidth={2}
            dot={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export const TrendChart = React.memo(TrendChartImpl)

interface BreakdownChartProps {
  buckets: ProfitabilityBucket[]
  currency?: string
  height?: ChartHeight
}

const BreakdownChartImpl = ({
  buckets,
  currency = "USD",
  height = DEFAULT_CHART_HEIGHT
}: BreakdownChartProps) => {
  const { t } = useTranslation()

  if (!buckets.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("portfolio.chart.empty", "No data for this window")}
      />
    )
  }

  const formatCurrency = (value: number) =>
    budgetFormat(value, { currency, notation: "compact" })

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={buckets} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={neutral.border} vertical={false} />
        <XAxis
          dataKey="project"
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={{ stroke: neutral.border }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={formatCurrency}
          tick={{ fontSize: 12, fill: neutral.faint }}
          axisLine={false}
          tickLine={false}
          width={72}
        />
        <RechartsTooltip
          formatter={(value) => formatCurrency(Number(value))}
          contentStyle={{
            borderRadius: 8,
            border: `1px solid ${neutral.border}`,
            boxShadow: "none",
          }}
        />
        <Legend
          verticalAlign="top"
          align="right"
          iconType="circle"
          wrapperStyle={{ fontSize: 12, paddingBottom: 10 }}
        />
        <Bar dataKey="revenue" name={t("portfolio.chart.revenue", "Revenue")} fill={chartSeries.cashIn} radius={[4, 4, 0, 0]} />
        <Bar dataKey="cost" name={t("portfolio.chart.cost", "Cost")} fill={chartSeries.cashOut} radius={[4, 4, 0, 0]} />
        <Line
          type="monotone"
          dataKey="margin"
          name={t("portfolio.chart.margin", "Margin")}
          stroke={chartSeries.net}
          strokeWidth={2}
          dot={{ r: 3 }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export const BreakdownChart = React.memo(BreakdownChartImpl)

const ENGAGEMENT_GROUPS: EngagementGroup[] = ["week", "month", "quarter", "year"]

/** How far back each grouping looks, ending today: eight weeks, twelve months,
 *  four quarters or three years, each starting on its period's first day. */
export const engagementLookback = (group: EngagementGroup, today: Dayjs): [string, string] => {
  const day = today.startOf("day")
  const start =
    group === "week"
      ? day.subtract((day.day() + 6) % 7, "day").subtract(7, "week")
      : group === "month"
        ? day.startOf("month").subtract(11, "month")
        : group === "quarter"
          ? day.month(Math.floor(day.month() / 3) * 3).startOf("month").subtract(9, "month")
          : day.startOf("year").subtract(2, "year")
  return [start.format("YYYY-MM-DD"), day.format("YYYY-MM-DD")]
}

interface LoadedPeriod {
  data: EngagementData | null
  loading: boolean
  failed?: boolean
}
const CUSTOM = "custom" as const
type EngagementMode = EngagementGroup | typeof CUSTOM

type Translate = (key: string, fallback: string, options?: Record<string, unknown>) => string

const periodLabeller = (locale: string | undefined, group: EngagementGroup, t: Translate) => {
  const day = new Intl.DateTimeFormat(locale, { month: "short", day: "numeric" }).format
  const month = new Intl.DateTimeFormat(locale, { month: "short", year: "numeric" }).format
  return (row: EngagementGroupRow): string => {
    const start = parseBucketDate(row.start)
    if (group === "month") return month(start)
    if (group === "quarter")
      return t("portfolio.chart.engagement.quarterLabel", "Q{{quarter}} {{year}}", {
        quarter: Math.floor(start.getMonth() / 3) + 1,
        year: start.getFullYear()
      })
    if (group === "year") return String(start.getFullYear())
    return `${day(start)} – ${day(parseBucketDate(row.end))}`
  }
}

const rangeLabeller = (locale: string | undefined) => {
  const full = new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    year: "numeric"
  }).format
  return (start: string, end: string) =>
    `${full(parseBucketDate(start))} – ${full(parseBucketDate(end))}`
}

const shareOf = (part: number, whole: number): number | null =>
  whole > 0 ? Math.round((part / whole) * 100) : null

interface EngagementTableProps {
  data: EngagementData
  onDrill?: (drill: EngagementDrill) => void
  loadRange?: (start: string, end: string) => Promise<EngagementData | null>
}

interface EngagementTableRow {
  key: string
  period: string
  start: string
  end: string
  added: number
  engaged: number
  rate: number | null
}

interface CustomPeriod {
  start: string
  end: string
  data: EngagementData | null
  loading: boolean
  failed?: boolean
}

const exportEngagementRows = (rows: EngagementTableRow[]) =>
  triggerCsvDownload(
    "leads-added-vs-engaged.csv",
    [
      ["period", "period_start", "period_end", "added", "engaged", "engagement_rate"],
      ...rows.map((row) => [row.period, row.start, row.end, row.added, row.engaged, row.rate ?? ""])
    ]
      .map((cells) => cells.map(csvEscape).join(","))
      .join("\r\n")
  )

/** Leads Added vs. Engaged as a short comparison table, grouped by the week,
 *  month or quarter the viewer picks — or by a period of their own, grouped by
 *  week under eight weeks and by month otherwise (the server's default). The
 *  rate is the share of a period's added leads that have met, whenever they
 *  met, so it never exceeds 100%. A count opens the leads behind it. */
const EngagementTableImpl = ({ data, onDrill, loadRange }: EngagementTableProps) => {
  const { t, i18n } = useTranslation()
  // With a loader each grouping looks back over its own span; without one the
  // table can only regroup the page's period.
  const [mode, setMode] = useState<EngagementMode>(
    loadRange ? "month" : data.defaultGroup ?? "week"
  )
  const [custom, setCustom] = useState<CustomPeriod | null>(null)
  const [loaded, setLoaded] = useState<Partial<Record<EngagementGroup, LoadedPeriod>>>({})
  const latestRequest = useRef(0)
  // A loader belongs to one set of filters (and one refresh); an answer from an
  // earlier loader must not land after a newer one's.
  const loaderGeneration = useRef(0)
  const customDates = useRef<{ start: string; end: string } | null>(null)

  const loadCustom = async (
    loader: NonNullable<EngagementTableProps["loadRange"]>,
    start: string,
    end: string
  ) => {
    const request = ++latestRequest.current
    customDates.current = { start, end }
    setCustom({ start, end, data: null, loading: true })
    const answer = await Promise.resolve()
      .then(() => loader(start, end))
      .catch(() => null)
    // Only the newest pick may land: an older, slower answer must not repaint it.
    if (request !== latestRequest.current) return
    setCustom({ start, end, data: answer ?? null, loading: false, failed: !answer })
  }

  // New filters or a refresh mean a new loader: every span is read again, a
  // picked period included.
  useEffect(() => {
    loaderGeneration.current += 1
    setLoaded({})
    if (loadRange && customDates.current) {
      const { start, end } = customDates.current
      void loadCustom(loadRange, start, end)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadRange])

  useEffect(() => {
    if (!loadRange || mode === CUSTOM || loaded[mode]) return
    const group = mode
    const generation = loaderGeneration.current
    const [start, end] = engagementLookback(group, dayjs())
    setLoaded((previous) => ({ ...previous, [group]: { data: null, loading: true } }))
    void Promise.resolve()
      .then(() => loadRange(start, end))
      .catch(() => null)
      .then((result) => {
        if (generation !== loaderGeneration.current) return
        setLoaded((previous) => ({
          ...previous,
          [group]: { data: result ?? null, loading: false, failed: !result }
        }))
      })
  }, [loadRange, mode, loaded])

  const spanLoading = mode !== CUSTOM && !!loadRange && (loaded[mode]?.loading ?? true)
  const failed = mode === CUSTOM ? !!custom?.failed : !!loadRange && !!loaded[mode]?.failed
  const shown =
    mode === CUSTOM
      ? custom?.data ?? null
      : loadRange
        ? loaded[mode]?.data ?? null
        : data
  const group: EngagementGroup =
    mode === CUSTOM ? shown?.defaultGroup ?? "week" : mode

  const pickRange = (dates: [Dayjs | null, Dayjs | null] | null) => {
    if (!loadRange || !dates?.[0] || !dates?.[1]) {
      latestRequest.current += 1
      customDates.current = null
      setCustom(null)
      return
    }
    void loadCustom(loadRange, dates[0].format("YYYY-MM-DD"), dates[1].format("YYYY-MM-DD"))
  }

  const retry = () => {
    if (mode === CUSTOM) {
      if (loadRange && custom) void loadCustom(loadRange, custom.start, custom.end)
      return
    }
    setLoaded((previous) => {
      const next = { ...previous }
      delete next[mode]
      return next
    })
  }

  const label = periodLabeller(i18n?.language, group, t as Translate)
  const fullRange = rangeLabeller(i18n?.language)
  // Leading periods with nothing in them are dropped: an account that began in
  // July shows July onward, not a run of empty months before it.
  const allRows = shown?.groups?.[group] ?? []
  const firstWithData = allRows.findIndex((row) => row.new > 0 || row.engaged > 0)
  const periodRows = firstWithData > 0 ? allRows.slice(firstWithData) : allRows
  const rows: EngagementTableRow[] = periodRows.map((row) => ({
    key: row.start,
    period: label(row),
    start: row.start,
    end: row.end,
    added: row.new,
    engaged: row.engaged,
    rate: shareOf(row.cohort_engaged, row.new)
  }))
  if (shown && periodRows.length) {
    rows.push({
      key: "total",
      period: t("portfolio.chart.engagement.total", "Total"),
      start: periodRows[0].start,
      end: periodRows[periodRows.length - 1].end,
      added: shown.totals.new,
      engaged: shown.totals.engaged,
      rate: shareOf(shown.totals.cohort_engaged ?? 0, shown.totals.new)
    })
  }

  const renderCount = (
    kind: EngagementDrill["kind"],
    value: number,
    row: EngagementTableRow
  ): React.ReactNode =>
    value > 0 && onDrill ? (
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: "auto", fontWeight: "inherit" }}
        onClick={() =>
          onDrill({
            kind,
            start: row.start,
            end: row.end,
            label: row.key === "total" ? fullRange(row.start, row.end) : row.period
          })
        }
      >
        {value}
      </Button>
    ) : (
      value
    )

  const modeLabels: Record<EngagementMode, string> = {
    week: t("portfolio.chart.engagement.week", "Week"),
    month: t("portfolio.chart.engagement.month", "Month"),
    quarter: t("portfolio.chart.engagement.quarter", "Quarter"),
    year: t("portfolio.chart.engagement.year", "Year"),
    custom: t("portfolio.chart.engagement.custom", "Custom")
  }
  const modes: EngagementMode[] = loadRange ? [...ENGAGEMENT_GROUPS, CUSTOM] : ENGAGEMENT_GROUPS

  const isEmpty = !shown || (shown.totals.new === 0 && shown.totals.engaged === 0)
  const waiting = (mode === CUSTOM && custom?.loading) || spanLoading
  const showsTable = !(mode === CUSTOM && !custom) && !waiting && !failed && !isEmpty
  const exportLabel = t("portfolio.chart.engagement.export", "Export CSV")

  return (
    <Flex vertical gap={8}>
      <Flex gap={8} wrap align="center">
        <Segmented<EngagementMode>
          size="small"
          value={mode}
          onChange={setMode}
          options={modes.map((value) => ({ value, label: modeLabels[value] }))}
        />
        {mode === CUSTOM && (
          <DatePicker.RangePicker
            size="small"
            value={custom ? [dayjs(custom.start), dayjs(custom.end)] : null}
            onChange={(dates) => pickRange(dates)}
          />
        )}
        {showsTable && (
          <Button
            size="small"
            type="text"
            icon={<DownloadOutlined />}
            aria-label={exportLabel}
            title={exportLabel}
            style={{ marginInlineStart: "auto" }}
            onClick={() => exportEngagementRows(rows)}
          />
        )}
      </Flex>
      {mode === CUSTOM && !custom ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(
            "portfolio.chart.engagement.pickPeriod",
            "Pick a start and end date. Under eight weeks shows weeks; longer shows months."
          )}
        </Typography.Text>
      ) : waiting ? (
        <Skeleton active paragraph={{ rows: 3 }} title={false} />
      ) : failed ? (
        <Flex vertical align="center" gap={8} style={{ padding: "16px 0" }}>
          <Typography.Text type="secondary">
            {t("portfolio.chart.engagement.loadFailed", "These figures could not be loaded.")}
          </Typography.Text>
          <Button size="small" onClick={retry}>
            {t("portfolio.chart.engagement.retry", "Retry")}
          </Button>
        </Flex>
      ) : isEmpty ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t("portfolio.chart.empty", "No data for this window")}
        />
      ) : (
        <Table<EngagementTableRow>
          size="small"
          pagination={false}
          dataSource={rows}
          onRow={(row) => (row.key === "total" ? { style: { fontWeight: 600 } } : {})}
          columns={[
            {
              title: t("portfolio.chart.engagement.period", "Period"),
              dataIndex: "period"
            },
            {
              title: t("portfolio.chart.engagement.added", "Added"),
              dataIndex: "added",
              align: "right",
              render: (value: number, row: EngagementTableRow) =>
                renderCount("added", value, row)
            },
            {
              title: t("portfolio.chart.engagement.engaged", "Engaged"),
              dataIndex: "engaged",
              align: "right",
              render: (value: number, row: EngagementTableRow) =>
                renderCount("engaged", value, row)
            },
            {
              title: t("portfolio.chart.engagement.rateColumn", "Engagement rate"),
              dataIndex: "rate",
              align: "right",
              render: (value: number | null) => (value == null ? "—" : `${value}%`)
            }
          ]}
        />
      )}
    </Flex>
  )
}

export const EngagementTable = React.memo(EngagementTableImpl)

const EMPTY_SERIES: CashFlowPoint[] = []
const EMPTY_STATUS: ProjectStatusData = { on_track: 0, at_risk: 0, delayed: 0, total: 0 }

const isDonut = (visualisation?: string): boolean =>
  visualisation === "donut" || visualisation === "pie"

const asTrendVariant = (visualisation?: string): TrendVariant =>
  visualisation === "area" || visualisation === "bar" ? visualisation : "line"

const asCashFlowVariant = (visualisation?: string): CashFlowVariant =>
  visualisation === "line" || visualisation === "area" ? visualisation : "bar"

export const ChartWidget = ({
  payload,
  fillHeight,
  onEngagementDrill,
  loadEngagementRange
}: WidgetProps) => {
  const { t } = useTranslation()
  const chart = payload?.chart
  const visualisation = chart?.visualisation
  const height: ChartHeight | undefined = fillHeight ? "100%" : undefined

  if (chart && "marketing" in chart) {
    return chart.marketing ? (
      <MarketingSourceTable initial={chart.marketing} onDrill={onEngagementDrill} />
    ) : (
      <Typography.Text type="secondary">
        {t("marketingSourcePerformance.unavailable")}
      </Typography.Text>
    )
  }

  if (chart?.engagement) {
    return (
      // Keyed so a new period starts from the grouping it suggests rather than
      // keeping the one picked for the previous period.
      <EngagementTable
        key={chart.engagement.defaultGroup}
        data={chart.engagement}
        onDrill={onEngagementDrill}
        loadRange={loadEngagementRange}
      />
    )
  }

  if (chart?.trend) {
    return (
      <TrendChart
        series={chart.trend.series}
        currency={chart.trend.currency}
        variant={asTrendVariant(visualisation)}
        compareSeries={chart.trend.compareSeries}
        compareLabel={chart.trend.compareLabel}
        height={height}
      />
    )
  }

  if (chart?.breakdown) {
    return (
      <BreakdownChart
        buckets={chart.breakdown.buckets}
        currency={chart.breakdown.currency}
        height={height}
      />
    )
  }

  if (isDonut(visualisation)) {
    return (
      <ProjectStatusDonut data={chart?.status ?? EMPTY_STATUS} height={height} />
    )
  }

  return (
    <CashFlowChart
      series={chart?.cashFlow?.series ?? EMPTY_SERIES}
      currency={chart?.cashFlow?.currency}
      variant={asCashFlowVariant(visualisation)}
      granularity={chart?.cashFlow?.granularity}
      asOf={chart?.cashFlow?.asOf}
      compareSeries={chart?.cashFlow?.compareSeries}
      compareLabel={chart?.cashFlow?.compareLabel}
      height={height}
    />
  )
}
